// src/pages/NossaHistoria.js
import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    Stack,
    Divider,
    Card,
    CardMedia,
    Chip,
    Button,
    useTheme,
    useMediaQuery,
} from "@mui/material";

import chefRicardo from "../assets/chefRicardo.jpeg";
import Header from "../components/Header";
import Footer from "../components/Footer";

import { Link } from "react-router-dom";

const YT_LINKS = [
    "https://youtu.be/ggOFPfEEZ9k?si=wVh0DAcZeCQ51A0C",
    "https://youtu.be/eAf4bwcFkHw?si=NXiTsR3st3LupbFf",
    "https://youtu.be/OFfT-8HptZg?si=3RGUYh9oMP6mjIn1",
    "https://youtu.be/4xU-fUSLOeQ?si=ej8kXAXq10kPDZf7",
];

function toEmbedUrl(url) {
    try {
        const u = new URL(url);
        if (u.hostname.includes("youtu.be")) {
            const id = u.pathname.replace("/", "").trim();
            return `https://www.youtube.com/embed/${id}`;
        }
        if (u.hostname.includes("youtube.com")) {
            const id = u.searchParams.get("v");
            if (id) return `https://www.youtube.com/embed/${id}`;
        }
    } catch (e) { }
    return url;
}

function VideoCard({ url, title }) {
    const embed = toEmbedUrl(url);

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 4,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.06)",
                bgcolor: "var(--offwhite)",
                transition: "transform var(--transition), box-shadow var(--transition)",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 14px 28px rgba(0,0,0,0.10)",
                },
            }}
        >
            <Box sx={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
                <Box
                    component="iframe"
                    src={embed}
                    title={title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        border: 0,
                    }}
                />
            </Box>

            <Box sx={{ p: 2.2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 950, mb: 0.4, color: "var(--cinza)" }}>
                    {title}
                </Typography>

            </Box>
        </Paper>
    );
}

export default function NossaHistoria() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    return (
        <>
            <Header />

            <Box sx={{ minHeight: "100vh", bgcolor: "var(--offwhite)" }}>
                {/* HERO */}
                <Box
                    sx={{
                        position: "relative",
                        py: { xs: 15, md: 12 },
                        paddingTop: 20,
                        bgcolor: "var(--azulEscuro)",
                        color: "#fff",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0.22,
                            background:
                                "radial-gradient(circle at 50% 35%, rgba(255,255,255,0.18), transparent 62%)",
                        }}
                    />

                    <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
                        <Stack spacing={3} alignItems="center">
                            <Chip
                                label="Uai Thai • Tiradentes"
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.12)",
                                    color: "#fff",
                                    fontWeight: 800,
                                    backdropFilter: "blur(10px)",
                                    border: "1px solid rgba(255,255,255,0.10)",
                                }}
                            />

                            <Typography
                                variant="h1"
                                sx={{
                                    fontWeight: 950,
                                    fontSize: { xs: "2.4rem", md: "4rem" },
                                    lineHeight: 1.05,
                                    letterSpacing: "-0.03em",
                                }}
                            >
                                Onde a Tailândia <br />
                                <Box component="span" sx={{ color: "var(--offWhite)" }}>
                                    abraça Minas.
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    color: "rgba(255,255,255,0.82)",
                                    fontSize: { xs: "1rem", md: "1.2rem" },
                                    maxWidth: 740,
                                    lineHeight: 1.7,
                                }}
                            >
                                Uma jornada sensorial que une a complexidade aromática do sudeste asiático ao calor e
                                hospitalidade das montanhas mineiras.
                            </Typography>

                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                sx={{ pt: 2, width: "100%", justifyContent: "center" }}
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    href="#funcionamento"
                                    sx={{
                                        borderRadius: 999,
                                        px: 4,
                                        py: 1.5,
                                        fontWeight: 900,
                                        bgcolor: "#fff",
                                        color: "var(--azulEscuro)",
                                        "&:hover": { bgcolor: "rgba(255,255,255,0.92)" },
                                    }}
                                >
                                    Ver funcionamento
                                </Button>

                                <Button
                                    variant="outlined"
                                    size="large"
                                    href="#videos"
                                    sx={{
                                        borderRadius: 999,
                                        px: 4,
                                        py: 1.5,
                                        fontWeight: 900,
                                        color: "#fff",
                                        borderColor: "rgba(255,255,255,0.30)",
                                        "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
                                    }}
                                >
                                    Nossa cozinha
                                </Button>
                            </Stack>
                        </Stack>
                    </Container>
                </Box>

                <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
                    {/* HISTÓRIA (e os 2 boxes centralizados verticalmente dentro do card) */}
                    <Grid container spacing={4} alignItems="stretch">
                        <Grid item xs={12} sx={{ display: "flex" }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 3, md: 5 },
                                    borderRadius: 4,
                                    bgcolor: "#fff",
                                    border: "1px solid rgba(0,0,0,0.06)",
                                    boxShadow: "0 6px 26px rgba(0,0,0,0.04)",
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    minHeight: { md: 520 }, // dá área pra centralizar os dois boxes
                                }}
                            >
                                {/* topo */}
                                <Box>
                                    <Typography
                                        variant="overline"
                                        sx={{ fontWeight: 900, letterSpacing: 1, color: "var(--vermelho)" }}
                                    >
                                        Nossa essência
                                    </Typography>

                                    <Typography
                                        variant="h4"
                                        sx={{ fontWeight: 950, mt: 1, mb: 2, color: "var(--cinza)", textAlign: "center" }}
                                    >
                                        Equilíbrio e paixão
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: "var(--text-muted)",
                                            lineHeight: 1.9,
                                            fontSize: "1.05rem",
                                            textAlign: "center",
                                            maxWidth: 980,
                                            mx: "auto",
                                        }}
                                    >
                                        O <strong>Uai Thai</strong> nasce de um desejo ousado: traduzir a alma tailandesa
                                        com sotaque mineiro. Aqui, o picante, o doce, o ácido e o salgado dançam juntos no
                                        mesmo prato, respeitando ingredientes frescos escolhidos a dedo.
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: "var(--text-muted)",
                                            lineHeight: 1.9,
                                            fontSize: "1.05rem",
                                            mt: 2,
                                            textAlign: "center",
                                            maxWidth: 980,
                                            mx: "auto",
                                        }}
                                    >
                                        Mais do que refeições, servimos memórias. Cada detalhe é pensado para que você se
                                        sinta em casa e viaje pelo paladar.
                                    </Typography>
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                {/* AQUI centraliza verticalmente os dois cards */}
                                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                                    <Grid
                                        container
                                        spacing={3}
                                        justifyContent="center"
                                        alignItems="center"
                                        sx={{ width: "100%" }}
                                    >
                                        <Grid item xs={12} md={6} lg={5}>
                                            <Box
                                                sx={{
                                                    p: 3,
                                                    bgcolor: "var(--offwhite)",
                                                    borderRadius: 3,
                                                    border: "1px solid var(--input-border)",
                                                    height: "100%",
                                                }}
                                            >
                                                <Typography variant="h6" sx={{ fontWeight: 950, mb: 1, color: "var(--cinza)" }}>
                                                    Identidade e técnica
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: "var(--text-muted)", lineHeight: 1.7 }}>
                                                    Cozinha autoral que respeita as bases asiáticas e valoriza o melhor da terra
                                                    mineira.
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={6} lg={5}>
                                            <Box
                                                sx={{
                                                    p: 3,
                                                    bgcolor: "var(--offwhite)",
                                                    borderRadius: 3,
                                                    border: "1px solid var(--input-border)",
                                                    height: "100%",
                                                }}
                                            >
                                                <Typography variant="h6" sx={{ fontWeight: 950, mb: 1, color: "var(--cinza)" }}>
                                                    Experiência intimista
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: "var(--text-muted)", lineHeight: 1.7 }}>
                                                    Poucas mesas, atendimento próximo e um clima acolhedor para celebrar momentos
                                                    especiais.
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* CHEF + FUNCIONAMENTO (lado a lado) */}
                    <Box id="funcionamento" sx={{ mt: { xs: 6, md: 8 } }}>
                        <Grid
                            container
                            spacing={4}
                            alignItems="stretch"
                            wrap={isMobile ? "wrap" : "nowrap"} // <- chave: desktop não quebra
                        >
                            {/* Foto do chef */}
                            <Grid
                                item
                                xs={12}
                                md={5}
                                sx={{
                                    display: "flex",
                                    flex: isMobile ? "1 1 auto" : "0 0 42%", // <- força largura no desktop
                                    maxWidth: isMobile ? "100%" : "42%",
                                }}
                            >
                                <Card
                                    elevation={0}
                                    sx={{
                                        borderRadius: 4,
                                        overflow: "hidden",
                                        width: "100%",
                                        border: "1px solid rgba(0,0,0,0.06)",
                                        boxShadow: "0 6px 26px rgba(0,0,0,0.04)",
                                        position: "relative",
                                        minHeight: { xs: 360, md: 520 },
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        image={chefRicardo}
                                        alt="Chef Ricardo"
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            objectPosition: "center top",
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            background:
                                                "linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.10) 55%, rgba(0,0,0,0.05) 100%)",
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            position: "absolute",
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            p: 3,
                                            color: "#fff",
                                        }}
                                    >
                                        <Chip
                                            label="Chef Executivo"
                                            size="small"
                                            sx={{
                                                bgcolor: "var(--vermelho)",
                                                color: "#fff",
                                                fontWeight: 900,
                                                mb: 1,
                                                borderRadius: 999,
                                            }}
                                        />
                                        <Typography variant="h5" sx={{ fontWeight: 950 }}>
                                            Ricardo
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.92, mt: 1, lineHeight: 1.7 }}>
                                            Cozinhar é um ato de presença. Minha missão é entregar sabor com personalidade.
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid>

                            {/* Horários */}
                            <Grid
                                item
                                xs={12}
                                md={7}
                                sx={{
                                    display: "flex",
                                    flex: isMobile ? "1 1 auto" : "1 1 58%", // <- ocupa o resto
                                    maxWidth: isMobile ? "100%" : "58%",
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: { xs: 3, md: 5 },
                                        borderRadius: 4,
                                        bgcolor: "#fff",
                                        border: "1px solid rgba(0,0,0,0.06)",
                                        boxShadow: "0 6px 26px rgba(0,0,0,0.04)",
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        minHeight: { xs: "auto", md: 520 }, // <- mesma “altura base” do chef
                                    }}
                                >
                                    <Stack spacing={2.2} sx={{ width: "100%" }}>
                                        <Box>
                                            <Typography
                                                variant="overline"
                                                sx={{ fontWeight: 900, letterSpacing: 1, color: "var(--vermelho)" }}
                                            >
                                                Atendimento
                                            </Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 950, mt: 1, color: "var(--cinza)" }}>
                                                Horários de funcionamento
                                            </Typography>
                                            <Typography sx={{ color: "var(--text-muted)", mt: 1 }}>
                                                Funcionamos em horários de almoço e jantar de terça a sábado, e almoço aos domingos.
                                            </Typography>
                                        </Box>

                                        <Divider />

                                        <Grid >
                                            <Grid item xs={12} sm={6}>
                                                <Box
                                                    sx={{
                                                        p: 2.5,
                                                        borderRadius: 3,
                                                        bgcolor: "var(--offwhite)",
                                                        border: "1px solid var(--input-border)",
                                                        height: "100%",
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 950, color: "var(--cinza)", mb: 0.5 }}>
                                                        Terça à sábado
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: "var(--text-muted)", lineHeight: 1.8 }}>
                                                        Almoço: 13h às 15h30
                                                        <br />
                                                        Jantar: 19h às 22h30
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Box
                                                    sx={{
                                                        p: 2.5,
                                                        borderRadius: 3,
                                                        bgcolor: "var(--offwhite)",
                                                        border: "1px solid var(--input-border)",
                                                        height: "100%",
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 950, color: "var(--cinza)", mb: 0.5 }}>
                                                        Domingo
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: "var(--text-muted)", lineHeight: 1.8 }}>
                                                        Almoço: 13h às 15h30
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        <Box>
                                            <Chip
                                                label="Fechado às segundas"
                                                variant="outlined"
                                                sx={{
                                                    borderColor: "var(--vermelho)",
                                                    color: "var(--vermelho)",
                                                    fontWeight: 900,
                                                    borderRadius: 2,
                                                }}
                                            />
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* VÍDEOS (MAIORES) */}
                    <Box id="videos" sx={{ mt: { xs: 8, md: 12 } }}>
                        <Box sx={{ textAlign: "center", mb: 5, maxWidth: 650, mx: "auto" }}>
                            <Typography
                                variant="overline"
                                sx={{ fontWeight: 900, color: "var(--text-muted)", letterSpacing: 1.5 }}
                            >
                                Multimídia
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 950, mt: 1, color: "var(--cinza)" }}>
                                Sinta o clima
                            </Typography>
                            <Typography sx={{ mt: 1, color: "var(--text-muted)" }}>
                                Bastidores, preparos e um pouco da nossa rotina.
                            </Typography>
                        </Box>

                        <Grid container spacing={3.2} justifyContent="center">
                            {YT_LINKS.map((link, idx) => (
                                <Grid width={400} key={link}>
                                    {/* lg=4 => 3 por linha no desktop (bem maior) */}
                                    <VideoCard url={link} title={`Vídeo ${idx + 1}`} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    {/* CTA FINAL */}
                    <Paper
                        elevation={2}
                        sx={{
                            mt: 10,
                            p: { xs: 4, md: 6 },
                            bgcolor: "white",
                            color: "var(--cinza)",
                            borderRadius: 4,
                            textAlign: "center",
                            position: "relative",
                            overflow: "hidden",
                            border: "1px solid rgba(255,255,255,0.08)",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                opacity: 0.18,
                                background:
                                    "radial-gradient(circle at 50% 35%, rgba(255,255,255,0.18), transparent 62%)",
                            }}
                        />

                        <Box sx={{ position: "relative", zIndex: 1 }}>
                            <Typography variant="h4" sx={{ fontWeight: 950, mb: 2 }}>
                                Pronto para viver essa experiência?
                            </Typography>
                            <Typography sx={{ color: "var(--cinza)", mb: 4, maxWidth: 560, mx: "auto" }}>
                                Garanta sua mesa e permita-se surpreender pelos sabores do Uai Thai.
                            </Typography>

                            <Button
                                component={Link}
                                to="/reservas"
                                variant="contained"
                                size="large"
                                sx={{
                                    bgcolor: "var(--azulEscuro)",
                                    color: "#fff",
                                    fontWeight: 900,
                                    px: 5,
                                    py: 1.5,
                                    borderRadius: 999,
                                    fontSize: "1.05rem",
                                    "&:hover": { bgcolor: "#c91417" },
                                }}
                            >
                                Fazer reserva agora
                            </Button>
                        </Box>
                    </Paper>
                </Container>
            </Box>

            <Footer />
        </>
    );
}
